import React from 'react';
import { Registry, RequestFormProvider } from './registry.jsx';
import DOMPurify from 'dompurify';
import { PageProvider } from './PageContext.jsx';

function sanitizeProps(type, props){
  if(!props) return props;
  const sanitizeHtml = (html)=> DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ALLOWED_ATTR: [ 'href', 'title', 'target', 'rel', 'style', 'data-token' ],
    ALLOWED_TAGS: [ 'a', 'b', 'i', 'u', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'span', 'small' ]
  });
  // Only sanitize known html-carrying fields
  if(type === 'text') {
    // Text component uses 'text' prop (not 'html')
    if(typeof props.text === 'string') return { ...props, text: sanitizeHtml(props.text) };
    // Fallback for legacy 'html' prop
    if(typeof props.html === 'string') return { ...props, html: sanitizeHtml(props.html) };
    return props;
  }
  // Additional components that support rich text fields
  switch(type){
    case 'heading':
      if(typeof props.text === 'string') return { ...props, text: sanitizeHtml(props.text) };
      return props;
    case 'divider':
      if(typeof props.label === 'string') return { ...props, label: sanitizeHtml(props.label) };
      return props;
    case 'button':
      if(typeof props.label === 'string') return { ...props, label: sanitizeHtml(props.label) };
      return props;
    case 'codeRequestForm': {
      const out = { ...props };
      if(typeof props.nameLabel === 'string') out.nameLabel = sanitizeHtml(props.nameLabel);
      if(typeof props.emailLabel === 'string') out.emailLabel = sanitizeHtml(props.emailLabel);
      if(typeof props.submitLabel === 'string') out.submitLabel = sanitizeHtml(props.submitLabel);
      if(typeof props.submitLabelLoading === 'string') out.submitLabelLoading = sanitizeHtml(props.submitLabelLoading);
      if(typeof props.consentLabel === 'string') out.consentLabel = sanitizeHtml(props.consentLabel);
      if(typeof props.disclaimer === 'string') out.disclaimer = sanitizeHtml(props.disclaimer);
      return out;
    }
    case 'codeRequestName':
      if(typeof props.label === 'string') return { ...props, label: sanitizeHtml(props.label) };
      return props;
    case 'codeRequestEmail':
      if(typeof props.label === 'string') return { ...props, label: sanitizeHtml(props.label) };
      return props;
    case 'codeRequestConsent':
      if(typeof props.label === 'string') return { ...props, label: sanitizeHtml(props.label) };
      return props;
    case 'codeRequestDropdown': {
      const out = { ...props };
      if(typeof props.label === 'string') out.label = sanitizeHtml(props.label);
      return out;
    }
    case 'codeRequestSubmit': {
      const out = { ...props };
      if(typeof props.submitLabel === 'string') out.submitLabel = sanitizeHtml(props.submitLabel);
      if(typeof props.submitLabelLoading === 'string') out.submitLabelLoading = sanitizeHtml(props.submitLabelLoading);
      if(typeof props.label === 'string') out.label = sanitizeHtml(props.label);
      return out;
    }
    case 'codeDisplay': {
      const out = { ...props };
      if(typeof props.heading === 'string') out.heading = sanitizeHtml(props.heading);
      if(typeof props.hint === 'string') out.hint = sanitizeHtml(props.hint);
      if(typeof props.copyLabel === 'string') out.copyLabel = sanitizeHtml(props.copyLabel);
      return out;
    }
    case 'countdown': {
      const out = { ...props };
      if(typeof props.prefix === 'string') out.prefix = sanitizeHtml(props.prefix);
      if(typeof props.suffix === 'string') out.suffix = sanitizeHtml(props.suffix);
      if(typeof props.endedText === 'string') out.endedText = sanitizeHtml(props.endedText);
      if(typeof props.title === 'string') out.title = sanitizeHtml(props.title);
      return out;
    }
    default:
      return props;
  }
}

function renderNode(node) {
  if (!node) return null;
  
  // Special handling for root node - render its children directly
  if (node.type === 'root') {
    return node.children?.map((c, i) => <React.Fragment key={c.id || i}>{renderNode(c)}</React.Fragment>);
  }
  
  const Comp = Registry[node.type];
  if (!Comp) return null;
  const children = node.children?.map((c, i) => <React.Fragment key={c.id || i}>{renderNode(c)}</React.Fragment>);
  const safeProps = sanitizeProps(node.type, node.props);
  return <Comp props={safeProps}>{children}</Comp>;
}

export const PageRenderer = ({ tree, meta }) => {
  if (!tree?.root) return <div>Empty page</div>;
  return (
    <PageProvider value={meta || {}}>
      {/* Global provider so modular form parts work anywhere */}
      <RequestFormProvider>
        <div className="pb-root">{renderNode(tree.root)}</div>
      </RequestFormProvider>
    </PageProvider>
  );
};
